import moment from 'moment-timezone'
import { AiOutlineCheckCircle, AiOutlineClockCircle } from 'react-icons/ai'
import { HiOutlineExclamationCircle } from 'react-icons/hi'
import React from 'react'
import { Tooltip } from '@elevate_security/elevate-component-library'
import { Link } from './components/ImportDataModal/styles'

export const BEHAVIOR = {
  CLEAN_DESK: 'Clean Desk',
  MALWARE: 'Malware',
  PASSWORD_MANAGER: 'Password Manager',
  PASSWORD_SECURITY: 'Password Security',
  SIMULATED_PHISHING: 'Simulated Phishing',
  ACTUAL_PHISHING: 'Actual Phishing',
  SENSITIVE_DATA_HANDLING: 'Sensitive Data Handling',
  TRAINING: 'Training',
  DEVICE_SECURITY: 'Device Security',
  OPTIONAL_ACTIVITIES: 'Optional Activities',
  PHYSICAL_SECURITY: 'Physical Security',
  SECURE_BROWSING: 'Secure Browsing',
  PEOPLE: 'People',
  ACCESS_GROUPS: 'Access Groups', // Here and below will not show up in filters
  PASSWORD_ROTATION: 'Password Rotation',
  ACCESS_TERMINATION: 'Access Termination',
  REPORTED_PHISHING_ONLY: 'Reported Phishing Only',
  PASSWORD_STRENGTH: 'Password Strength'
}

export const BEHAVIORS = [
  {
    name: BEHAVIOR.CLEAN_DESK,
    description: 'How well are your individuals physically safeguarding sensitive data?'
  },
  {
    name: BEHAVIOR.MALWARE,
    description: 'Who is downloading and executing malware?'
  },
  {
    name: BEHAVIOR.PASSWORD_MANAGER,
    description: 'Who is using a password manager to store their credentials?'
  },
  {
    name: BEHAVIOR.PASSWORD_SECURITY,
    description: 'Who is using, reusing, or using weak passwords for SSO?'
  },
  {
    name: BEHAVIOR.SIMULATED_PHISHING,
    description: 'How is your org reacting to simulated phishing attacks?'
  },
  {
    name: BEHAVIOR.ACTUAL_PHISHING,
    description: 'Who is being targeted with real phishing attacks, and how are they reacting?'
  },
  {
    name: BEHAVIOR.SENSITIVE_DATA_HANDLING,
    description: 'Who in your org is using the wrong systems to share sensitive data?'
  },
  {
    name: BEHAVIOR.TRAINING,
    description: 'Who is completing trainings on time?'
  },
  {
    name: BEHAVIOR.OPTIONAL_ACTIVITIES,
    description: 'Who has taken part in optional activities?'
  },
  {
    name: BEHAVIOR.PHYSICAL_SECURITY,
    description: 'Who is likely to expose secrets in the physical environment?'
  },
  {
    name: BEHAVIOR.DEVICE_SECURITY,
    description: 'Who has risky device configurations or untrusted software?'
  },
  {
    name: BEHAVIOR.SECURE_BROWSING,
    description: 'Who has risky online browsing habits?'
  },
  {
    name: BEHAVIOR.PEOPLE,
    description: 'HR related information.'
  }
]

export const ALL_INTEGRATION_TABS = [
  'My Integrations',
  'Available Inbound Integrations',
  'Available Outbound integrations'
]

export const ASSESS_INTEGRATIONS_TABS = ['My Integrations', 'Available Integrations']

export const FILE_VALIDATION_STATUS_LABEL = {
  in_progress: 'Processing', // still running
  success: 'Success', // all data imported
  success_with_invalid_rows: 'Success', // some rows were imported, some were bad data and ignored
  failed: 'Failed', // technical issue, need to retry later
  invalid: 'Invalid File', // data is not acceptable, it needs to be fixed, do not retry
  not_configured: 'Not Available' // stream is not configured to process file yet
}

export const FILE_STATUS_COLOR = {
  in_progress: 'yellow',
  success: 'green',
  success_with_invalid_rows: 'green',
  failed: 'red',
  invalid: 'red',
  not_configured: 'yellow'
}

/**
 * Map connector info in the same structure as each
 * vendor in the list above.
 * We want to do that because we dont want to alter
 * the structure that we're using in many places to
 * show vendor data
 *
 * @param {connector} connector to exactract info.

 */
export const mapConnectorstoVendorList = (connector) => {
  const {
    vendor: { id, name, code, logo, supported }
  } = connector
  const { connector_features, connector_type } = connector
  const tags = connector_features.map(({ integration_format }) => {
    const { integration_type } = integration_format
    return integration_type
  })
  const { connector_credential_fields, doc_url } = connector
  const integrationInputs = connector_credential_fields.map(
    ({ display_name, identifier, is_secret, is_shared, is_optional, default_value }) => ({
      fullName: display_name,
      identifier,
      secret: is_secret,
      is_shared,
      is_optional,
      default_value
    })
  )
  return {
    id,
    name,
    code,
    logo,
    supported,
    tags: tags.sort(),
    isVendor: true,
    integrationInputs,
    apiDocLink: doc_url,
    connector_features,
    connector_type
  }
}

export const mapIntegrationStream = ({
  id,
  vendor,
  integration_type,
  status,
  last_received_date,
  channel,
  stream_id,
  connector_feature_id,
  connector_type,
  connector,
  has_transformation_configs,
  api_error_response,
  api_pull_details,
  auto_import_integration_format,
  alternate_name
}) => {
  const { logo, name } = vendor
  const { display_name, code, channel_type } = channel
  // eslint-disable-next-line no-param-reassign
  connector = mapConnectorstoVendorList(connector)
  return {
    id,
    name,
    logo,
    integration_type,
    last_received_date,
    display_name,
    code,
    vendor,
    status,
    channel_type,
    stream_id,
    connector_feature_id,
    connector_type,
    connector,
    has_transformation_configs,
    api_error_response,
    api_pull_details,
    auto_import_integration_format,
    alternate_name
  }
}

export const formatDateForUI = (date) => {
  const dateObj = new Date(date)
  const month = dateObj.toLocaleString('default', { month: 'long' })
  const day = dateObj.getUTCDate()
  const year = dateObj.getUTCFullYear()
  const hours = dateObj.getUTCHours() >= 12 ? dateObj.getUTCHours() - 12 : dateObj.getUTCHours()
  const hoursFormatted = hours > 9 ? hours : `0${hours}`
  const minutes =
    dateObj.getUTCMinutes() > 9 ? dateObj.getUTCMinutes() : `0${dateObj.getUTCMinutes()}`
  const amOrPm = dateObj.getUTCHours() >= 12 ? 'PM' : 'AM'
  return `${month} ${day}, ${year} at ${hoursFormatted}:${minutes} ${amOrPm} \nUTC`
}

export const formatDate = (date) => moment(date).format('MMMM D, YYYY [at] hh:mm A')

export const formatDateTimezone = (date, default_timezone) => {
  const timezone = default_timezone || 'UTC'
  const moment_date = moment(date).tz(timezone)
  return moment_date.format('MMMM DD, YYYY [at] hh:mm A Z')
}

export const mapDataset = ({ s3_key, last_modified, ...rest }, default_timezone) => ({
  s3_key: s3_key.split('/')[s3_key.split('/').length - 1],
  last_modified_utc: formatDateForUI(last_modified),
  last_modified: formatDateTimezone(last_modified, default_timezone),
  ...rest
})

export const mapHRImportDataset = ({ s3_key, last_modified, ...rest }, default_timezone) => ({
  s3_key,
  file_name: s3_key.split('/')[s3_key.split('/').length - 1],
  last_modified_utc: formatDateForUI(last_modified),
  last_modified: formatDateTimezone(last_modified, default_timezone),
  ...rest
})

export const getConnectorFeatureID = (connector_features_list, behavior) => {
  const connector = connector_features_list.find(
    ({ integration_format: { integration_type } }) => integration_type === behavior
  )
  const { id } = connector
  return id
}

export const getConnectorFeatureSupportMethods = (connector_features_list, behavior) => {
  const connector = connector_features_list.find(
    ({ integration_format: { integration_type } }) => integration_type === behavior
  )
  // returning empty array, In case the behaviour is not supported for this connector
  return connector?.enabled_methods || []
}

export const enableUpload = (channelCode) => ['UI'].includes(channelCode)

export const BEHAVIOR_TOOLTIPS = {
  [BEHAVIOR.CLEAN_DESK]: 'This behavior is no longer supported for new integrations.',
  [BEHAVIOR.MALWARE]: (
    <p>
      Data requirements and format:{' '}
      <Link
        target="_blank"
        href="https://elevate-security.stoplight.io/docs/docs-integrations/30aua009al4bx-priority-malware-endpoint#data-requirements"
      >
        Malware
      </Link>
    </p>
  ),
  [BEHAVIOR.PASSWORD_MANAGER]: (
    <p>
      Data requirements and format:{' '}
      <Link
        target="_blank"
        href="https://elevate-security.stoplight.io/docs/docs-integrations/vix0yl7dbdtgj-password-manager-requirements#data-requirements"
      >
        Password Manager
      </Link>
    </p>
  ),
  [BEHAVIOR.PASSWORD_SECURITY]: 'This behavior is no longer supported for new integrations.',
  [BEHAVIOR.SIMULATED_PHISHING]: (
    <p>
      Data requirements and format:{' '}
      <Link
        target="_blank"
        href="https://elevate-security.stoplight.io/docs/docs-integrations/nmem8kp7rqwhs-simulated-phishing#data-requirements"
      >
        Simulated Phishing
      </Link>
    </p>
  ),
  [BEHAVIOR.ACTUAL_PHISHING]: (
    <p>
      Data requirements and format:{' '}
      <Link
        target="_blank"
        href="https://elevate-security.stoplight.io/docs/docs-integrations/ngjm8fz8sa8z2-priority-actual-phishing-email-gateway#data-requirements"
      >
        Actual Phishing
      </Link>
    </p>
  ),
  [BEHAVIOR.SENSITIVE_DATA_HANDLING]: (
    <p>
      Data requirements and format:{' '}
      <Link
        target="_blank"
        href="https://elevate-security.stoplight.io/docs/docs-integrations/rnfaavb7yvt9e-priority-sensitive-data-handling-data-loss-prevention#data-requirements"
      >
        Sensitive Data Handling
      </Link>
    </p>
  ),
  [BEHAVIOR.TRAINING]: (
    <p>
      Data requirements and format:{' '}
      <Link
        target="_blank"
        href="https://elevate-security.stoplight.io/docs/docs-integrations/gy7jjnabtweoz-training#data-requirements"
      >
        Training
      </Link>
    </p>
  ),
  [BEHAVIOR.OPTIONAL_ACTIVITIES]: (
    <p>
      Data requirements and format:{' '}
      <Link
        target="_blank"
        href="https://elevate-security.stoplight.io/docs/docs-integrations/tyyh9l04nr2v9-optional-activities#data-requirements"
      >
        Optional Activities
      </Link>
    </p>
  ),
  [BEHAVIOR.PHYSICAL_SECURITY]: 'This behavior is no longer supported for new integrations.',
  [BEHAVIOR.DEVICE_SECURITY]: 'This behavior is no longer supported for new integrations.',
  [BEHAVIOR.SECURE_BROWSING]: (
    <p>
      Data requirements and format:{' '}
      <Link
        target="_blank"
        href="https://elevate-security.stoplight.io/docs/docs-integrations/4vit0xvpxosy6-priority-secure-browsing-web-gateway-or-proxy#data-requirements"
      >
        Secure Browsing
      </Link>
    </p>
  ),
  [BEHAVIOR.PEOPLE]: (
    <p>
      Data requirements and format:{' '}
      <Link
        target="_blank"
        href="https://elevate-security.stoplight.io/docs/docs-integrations/72wqdfnvojw2y-required-people-hr#data-requirements"
      >
        People
      </Link>
    </p>
  ),
  [BEHAVIOR.ACCESS_GROUPS]: 'This behavior is no longer supported for new integrations.',
  [BEHAVIOR.PASSWORD_ROTATION]: 'This behavior is no longer supported for new integrations.',
  [BEHAVIOR.ACCESS_TERMINATION]: 'This behavior is no longer supported for new integrations.',
  [BEHAVIOR.REPORTED_PHISHING_ONLY]:
    'This behavior is no longer supported for new API push integrations.',
  [BEHAVIOR.PASSWORD_STRENGTH]: 'This behavior is no longer supported for new integrations.'
}

export const pushMethods = (dataType) => [
  {
    label: 'Import a single data file',
    value: 'UI',
    tooltipText: BEHAVIOR_TOOLTIPS[dataType],
    readonlyTooltip: true,
    docURL:
      'https://elevate-security.stoplight.io/docs/docs-integrations/nvy0il0enp96x-manual-upload'
  },
  {
    label: 'Push data through SFTP',
    value: 'SFTP',
    tooltipText: BEHAVIOR_TOOLTIPS[dataType],
    readonlyTooltip: true,
    docURL:
      'https://elevate-security.stoplight.io/docs/docs-integrations/x3sban07r847h-elevate-security-sftp'
  },
  {
    label: 'Elevate data push API',
    value: 'API_PUSH',
    tooltipText: BEHAVIOR_TOOLTIPS[dataType],
    readonlyTooltip: true,
    docURL:
      'https://elevate-security.stoplight.io/docs/docs-integrations/gzjr3jv74oxrc-elevate-security-api-post'
  },
  {
    label: 'Elevate data streaming API',
    value: 'API_PUSH_STREAM',
    tooltipText: 'Data will be streamed into the Elevate platform',
    readonlyTooltip: true,
    docURL:
      'https://elevate-security.stoplight.io/docs/docs-integrations/gzjr3jv74oxrc-elevate-security-api-post'
  }
]
export const isThisMultiIntegrationStreamProcess = (
  integrationStreams,
  behavior,
  vendor_code,
  channel_type
) => {
  const integrationStreamsForBehavior = getIntegrationStreamsForVendorBehavior(
    integrationStreams,
    behavior,
    vendor_code,
    channel_type
  )
  return integrationStreamsForBehavior.length > 0
}

export const getIntegrationStreamsForVendorBehavior = (
  integration_streams,
  behavior,
  vendor_code,
  channel_type
) => {
  const integrationStreams = integration_streams.filter(
    ({ integration_type, vendor, code }) =>
      integration_type === behavior && vendor.code === vendor_code && code === channel_type
  )
  return integrationStreams
}

export const getIntegrationStreamsForVendor = (integration_streams, vendorId, vendorCode) =>
  vendorCode === 'other'
    ? []
    : integration_streams
        .filter(({ vendor: { id } }) => id === vendorId)
        .map(({ integration_type }) => integration_type)

export const INTEGRATION_STATUS = {
  scheduled: 'yellow',
  success: 'green',
  failed: 'red'
}

export const INTEGRATION_ICONS = {
  scheduled: <AiOutlineClockCircle color="#959DA8" />,
  success: <AiOutlineCheckCircle color="green" />,
  failed: (
    <Tooltip
      body="Oh no! There’s an error with this integration. View more to see the error message."
      placement="top"
      size="xsm"
      distance="16px"
      theme="light-border"
      readOnly
    >
      <HiOutlineExclamationCircle color="red" />
    </Tooltip>
  )
}
export const enableEdit = (channelCode) =>
  !['API_PUSH', 'API_PUSH_STREAM', 'SFTP', 'UI'].includes(channelCode)
